import React from 'react';
import ReactMarkdown from 'react-markdown';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {Prism, SyntaxHighlighterProps} from 'react-syntax-highlighter';
import remarkGfm from 'remark-gfm'
import Image from 'next/image';

const SyntaxHighlighter = Prism as typeof React.Component<SyntaxHighlighterProps>;

export const MarkdownRenderer = (props: { markdown: string }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        code({ inline, className, children, ...props }: any) {
          const match = /language-(\w+)/.exec(className ?? '');
          if (!inline && match) {
            return (
              <SyntaxHighlighter 
                style={dracula}
                language={match[1]}
                PreTag="div"
                {...(props as SyntaxHighlighterProps)}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            );
          } else {
            return <code {...props}>{children}</code>;
          }
        },

        // Headings
        h1({ children }) {
          return <h1 className="text-3xl font-bold mt-8 mb-4">{children}</h1>;
        },
        h2({ children }) {
          return <h2 className="text-2xl font-bold mt-6 mb-3">{children}</h2>;
        },
        h3({ children }) {
          return <h3 className="text-xl font-bold mt-5 mb-2">{children}</h3>;
        },
        h4({ children }) {
          return <h4 className="text-lg font-bold mt-4 mb-2">{children}</h4>;
        },
        h5({ children }) {
          return <h5 className="text-base font-bold mt-4 mb-1">{children}</h5>;
        },
        h6({ children }) {
          return <h6 className="text-sm font-bold mt-3 mb-1">{children}</h6>;
        },
        // Images
        img({ src, alt }) {
          return (
            <Image
              src={typeof src === 'string' ? src : ''}
              alt={alt || ''}
              className="rounded-md my-4"
              width={184}
              height={184}
            />
          );
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        a({ href, children, ...props }: any) {
          return (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ 
                textDecoration: 'underline', 
                overflowWrap: 'break-word',
                wordBreak: 'break-all'
              }} 
              {...props}
            >
              {children}
            </a>
          );
        },

         // Lists
        ul({ children }) {
          return <ul className="list-disc pl-6">{children}</ul>;
        },
        ol({ children }) {
          return <ol className="list-decimal pl-6">{children}</ol>;
        },
        li({ children }) {
          return <li>{children}</li>;
        },

        // Blockquotes
        blockquote({ children }) {
          return (
            <blockquote className="border-l-4 border-muted-foreground pl-4 italic my-4 text-muted-foreground">
              {children}
            </blockquote>
          );
        },
        // Horizontal rule
        hr() {
          return <hr className="my-2 border-muted-foreground" />;
        },

        // Text formatting
        strong({ children }) {
          return <strong className="font-bold">{children}</strong>;
        },
        em({ children }) {
          return <em className="italic">{children}</em>;
        },
        del({ children }) {
          return <del className="line-through">{children}</del>;
        },
        small({ children }) {
          return <small className="text-xs text-muted-foreground">{children}</small>;
        },

        // Tables
        table({ children }) {
          return (
            <div className="overflow-x-auto my-4">
              <table className="table-auto border-collapse border border-muted-foreground w-full text-sm">
                {children}
              </table>
            </div>
          );
        },
        th({ children }) {
          return (
            <th className="border border-muted-foreground px-3 py-2 bg-muted-foreground font-semibold text-left">
              {children}
            </th>
          );
        },
        td({ children }) {
          return (
            <td className="border border-muted-foreground px-3 py-2">
              {children}
            </td>
          );
        },

         // Pre blocks (code containers)
        pre({ children }) {
            return <pre className="p-0 rounded-lg overflow-x-auto">{children}</pre>;
        },
      }}
      
    >
      {props.markdown}
    </ReactMarkdown>
  );
};
